import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, Download, Share2, Trash2, FileText, Lock, Languages, Award, Sparkles } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TokenPackPicker from "@/components/wallet/TokenPackPicker";
import MollieCardPayment from "@/components/wallet/MollieCardPayment";
import { canUseGooglePlayBilling } from "@/utils/pwaContext";
import { useI18n } from "@/i18n/I18nContext";
import { translateApiError } from "@/utils/apiErrors";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTokenPurchase } from "@/hooks/useTokenPurchase";
import api, { API } from "@/lib/api";
import { copyToClipboard } from "@/lib/utils";
import { ORDER } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

const PIPELINE = ["paid", "generating_lyrics", "generating_music", "generating_thumbnail", "completed"];
const TERMINAL = ["completed", "failed", "refunded"];
const DOWNLOAD_UNLOCK_COST = 10;

export default function OrderStatus() {
  const { orderId } = useParams();
  const { t, language } = useI18n();
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const { buyPack: purchasePack } = useTokenPurchase();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(1);
  const [showTranslated, setShowTranslated] = useState(false);
  const [showUnlockPacks, setShowUnlockPacks] = useState(false);
  const [packs, setPacks] = useState([5, 10, 20, 50, 100, 200]);
  const [loadingPack, setLoadingPack] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [pollTrigger, setPollTrigger] = useState(0);

  const fetchOrder = useCallback(async () => {
    const { data } = await api.get(`/orders/${orderId}`);
    setOrder(data);
    if (data.generated?.selected_version) setSelectedVersion(data.generated.selected_version);
  }, [orderId]);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setPacks(data.packs || [5, 10, 20, 50, 100, 200]));
  }, []);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(() => {
      setOrder((current) => {
        if (current && TERMINAL.includes(current.status)) {
          clearInterval(interval);
          return current;
        }
        fetchOrder();
        return current;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [fetchOrder, pollTrigger]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await api.post(`/orders/${orderId}/retry`);
      await fetchOrder();
      setPollTrigger((v) => v + 1); // restart polling since we just left the "failed" terminal state
      toast({ title: t("order.retryStarted") });
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setRetrying(false);
    }
  };

  const fileUrl = (path, forceDownload = false) => {
    const token = localStorage.getItem("ams_token");
    return `${API}/files/${path}?auth=${token}${forceDownload ? "&download=1" : ""}`;
  };

  const handleSelectVersion = async (v) => {
    setSelectedVersion(v);
    await api.post(`/orders/${orderId}/select-version`, { version_number: v });
  };

  const handleUnlockDownload = async () => {
    try {
      await api.post("/wallet/spend-unlock", { order_id: orderId });
      await refetch();
      fetchOrder();
      toast({ title: t("common.success") });
    } catch (err) {
      if (err?.response?.status === 400) {
        setShowUnlockPacks(true);
      } else {
        toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
      }
    }
  };

  const [selectedPack, setSelectedPack] = useState(null);

  const handleUnlockPaymentSuccess = async () => {
    setSelectedPack(null);
    await refetch();
    await api.post("/wallet/spend-unlock", { order_id: orderId });
    await refetch();
    setShowUnlockPacks(false);
    fetchOrder();
    toast({ title: t("common.success") });
  };

  const handleBuyPackForUnlock = async (pack) => {
    // Android/TWA: unchanged, native Google Play Billing purchase sheet.
    if (await canUseGooglePlayBilling()) {
      setLoadingPack(pack);
      try {
        const data = await purchasePack(pack, { resumeOrderId: orderId, resumeAction: "unlock_download" });
        if (data.status === "paid") await handleUnlockPaymentSuccess();
      } catch (err) {
        if (err?.playBillingFallback) {
          // Google Play genuinely failed (timeout/SecurityError) - fall back to the embedded
          // Mollie card form instead of a dead-end error toast (16/08/2026, policy-compliant).
          toast({ title: t("checkout.playBillingFallbackTitle"), description: t("checkout.playBillingFallbackDesc") });
          setSelectedPack(pack);
        } else if (err?.isPurchaseCancelled) {
          toast({ title: t("checkout.playCancelledTitle"), description: t("checkout.playCancelledDesc") });
        } else {
          toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) || err?.message });
        }
      } finally {
        setLoadingPack(null);
      }
      return;
    }
    // Web: reveal the embedded Mollie card form right here instead of redirecting away.
    setSelectedPack(pack);
  };

  const handleDelete = async () => {
    if (!window.confirm(t("order.deleteConfirm"))) return;
    await api.delete(`/orders/${orderId}`);
    navigate("/dashboard");
  };

  const handleShare = async () => {
    const url = order?.generated?.share_token ? `${window.location.origin}/s/${order.generated.share_token}` : window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: order?.generated?.title, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    const copied = await copyToClipboard(url);
    if (copied) {
      toast({ title: t("referral.linkCopied") });
    } else {
      toast({ title: order?.generated?.title, description: url });
    }
  };

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const stepIndex = PIPELINE.indexOf(order.status);
  const isFailed = order.status === "failed";
  const isCompleted = order.status === "completed";
  const versions = order.generated?.versions || [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SEO title="Suivi de ma chanson | Kalypso Music" description="Suivez en direct la création de votre chanson personnalisée Kalypso Music." path="/order" noindex />
        <div className="text-center">
          <h1 className="font-heading text-2xl font-medium">{t("order.trackingTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("order.trackingSubtitle")}</p>
          <Badge data-testid={ORDER.statusBadge} className="mt-4 rounded-full px-4 py-1.5" variant={isFailed ? "destructive" : "default"}>
            {t(`order.status${order.status.charAt(0).toUpperCase()}${order.status.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
          </Badge>
        </div>

        {!isFailed && !isCompleted && (
          <div className="mt-10 flex items-center justify-between">
            {PIPELINE.slice(0, 4).map((s, i) => (
              <div key={s} className="flex flex-1 flex-col items-center gap-2">
                {i < stepIndex ? (
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                ) : i === stepIndex ? (
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                ) : (
                  <Circle className="h-6 w-6 text-muted-foreground/40" />
                )}
                <span className="hidden text-center text-[11px] text-muted-foreground sm:block">
                  {t(`order.status${s.charAt(0).toUpperCase()}${s.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </span>
              </div>
            ))}
          </div>
        )}

        {isFailed && (
          <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
            <p>{t("order.errorNotice")}</p>
            <Button
              variant="outline"
              className="mt-4 rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid={ORDER.retryBtn}
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {retrying ? t("order.retrying") : t("order.retryBtn")}
            </Button>
          </div>
        )}

        {isCompleted && (
          <div className="mt-10 space-y-6">
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              {order.generated.thumbnail_storage_path && (
                <img src={fileUrl(order.generated.thumbnail_storage_path)} alt={order.generated.title} className="h-64 w-full object-cover" />
              )}
              <div className="p-6">
                <h2 className="font-heading text-xl font-semibold">{order.generated.title}</h2>

                {versions.length > 1 && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">{t("order.selectVersion")}</p>
                    <div className="mt-2 flex gap-2">
                      {versions.map((v) => (
                        <Button
                          key={v.version_number}
                          size="sm"
                          variant={selectedVersion === v.version_number ? "default" : "outline"}
                          data-testid={ORDER.selectVersionBtn(v.version_number)}
                          className="rounded-full"
                          onClick={() => handleSelectVersion(v.version_number)}
                        >
                          {t("order.version")} {v.version_number}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <audio
                  data-testid={ORDER.player}
                  controls
                  controlsList="nodownload noplaybackrate"
                  onContextMenu={(e) => e.preventDefault()}
                  className="mt-4 w-full"
                  src={fileUrl(versions.find((v) => v.version_number === selectedVersion)?.storage_path)}
                />

                <div className="mt-5 flex flex-wrap gap-2">
                  {order.form.download_mp3_included ? (
                    <a href={fileUrl(versions.find((v) => v.version_number === selectedVersion)?.storage_path, true)} download data-testid={ORDER.downloadBtn}>
                      <Button variant="outline" className="rounded-full">
                        <Download className="mr-2 h-4 w-4" />
                        {t("order.downloadMp3")}
                      </Button>
                    </a>
                  ) : (
                    <Button variant="outline" className="rounded-full" data-testid={ORDER.unlockDownloadBtn} onClick={handleUnlockDownload}>
                      <Lock className="mr-2 h-4 w-4" />
                      {t("order.unlockDownload")}
                    </Button>
                  )}
                  <Button variant="outline" className="rounded-full" data-testid={ORDER.shareBtn} onClick={handleShare}>
                    <Share2 className="mr-2 h-4 w-4" />
                    {t("common.share")}
                  </Button>
                  <Button variant="ghost" className="rounded-full text-destructive" data-testid={ORDER.deleteBtn} onClick={handleDelete}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </Button>
                </div>

                {showUnlockPacks && (
                  <div className="mt-4 space-y-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
                    <p className="text-sm font-medium">{t("checkout.insufficientTitle")}</p>
                    <p className="text-xs text-muted-foreground">{t("checkout.insufficientDesc")}</p>
                    <TokenPackPicker
                      packs={packs}
                      missingTokens={Math.max(0, DOWNLOAD_UNLOCK_COST - (user?.wallet_tokens || 0))}
                      onBuy={handleBuyPackForUnlock}
                      loadingPack={loadingPack}
                    />
                    {selectedPack && (
                      <MollieCardPayment
                        pack={selectedPack}
                        resumeOrderId={orderId}
                        resumeAction="unlock_download"
                        onSuccess={handleUnlockPaymentSuccess}
                        onCancel={() => setSelectedPack(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {order.generated.lyrics && (
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading text-base font-medium">
                    <FileText className="h-4 w-4" />
                    {showTranslated && order.generated.translated_lyrics ? t("order.translatedLyricsTitle") : t("order.lyricsTitle")}
                  </h3>
                  {order.generated.translated_lyrics && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full"
                      data-testid={ORDER.translatedLyricsToggle}
                      onClick={() => setShowTranslated((v) => !v)}
                    >
                      <Languages className="mr-2 h-4 w-4" />
                      {showTranslated ? t("order.showOriginalLyrics") : t("order.showTranslatedLyrics")}
                    </Button>
                  )}
                </div>
                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">
                  {showTranslated && order.generated.translated_lyrics ? order.generated.translated_lyrics : order.generated.lyrics}
                </pre>
              </div>
            )}

            {order.generated.short_clip_storage_path && (
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-medium">
                  <Sparkles className="h-4 w-4" />
                  {t("order.shortClipTitle")}
                </h3>
                <audio data-testid={ORDER.shortClipPlayer} controls controlsList="nodownload noplaybackrate" onContextMenu={(e) => e.preventDefault()} className="mt-4 w-full" src={fileUrl(order.generated.short_clip_storage_path)} />
                <a href={fileUrl(order.generated.short_clip_storage_path, true)} download className="mt-3 inline-block">
                  <Button variant="outline" size="sm" className="rounded-full" data-testid={ORDER.shortClipDownloadBtn}>
                    <Download className="mr-2 h-4 w-4" />
                    {t("order.downloadShortClip")}
                  </Button>
                </a>
              </div>
            )}

            {order.generated.certificate_storage_path && (
              <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-medium">
                  <Award className="h-4 w-4" />
                  {t("order.certificateTitle")}
                </h3>
                <a href={fileUrl(order.generated.certificate_storage_path, true)} download>
                  <Button variant="outline" size="sm" className="rounded-full" data-testid={ORDER.certificateDownloadBtn}>
                    <Download className="mr-2 h-4 w-4" />
                    {t("order.downloadCertificate")}
                  </Button>
                </a>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
